/** Task list + task endpoints. */

import type { EffectiveAccess, SharedByView } from "./notes";
import { ApiClient } from "./client";
import type { TagResponse } from "./tags";

export type TaskPriority = "low" | "normal" | "high" | "urgent";

/** Re-export for callsites that import TagResponse via the tasks module. */
export type { TagResponse, EffectiveAccess, SharedByView };

export type RecordStatus = "active" | "archived" | "deleted";

/** Caller-relative scope for ``GET /task-lists``. ``"mine"`` returns only
 *  lists the caller owns, ``"shared"`` only lists shared *with* the
 *  caller, ``"all"`` (default) returns both. */
export type TasksScope = "mine" | "shared" | "all";

export type TaskListColor =
  | "purple"
  | "blue"
  | "green"
  | "orange"
  | "yellow"
  | "aqua"
  | "red"
  | "fg";

export const TASK_LIST_COLORS: readonly TaskListColor[] = [
  "purple",
  "blue",
  "green",
  "orange",
  "yellow",
  "aqua",
  "red",
  "fg",
];

export interface TaskListResponse {
  id: string;
  owner_id: string;
  name: string;
  /** Legacy: rebuilt from shares for compatibility. New code should use
   *  the share-aware fields below. */
  members: string[];
  created_at: string;
  updated_at: string;
  color: TaskListColor | null;
  description: string;
  default_tag_names: string[];
  /** Per-user: section assignment (`null` = unassigned). */
  section_id: string | null;
  /** Per-user: pin flag. */
  pinned: boolean;
  /** Per-user: ordering within section/pinned/unassigned bucket. */
  position: number;
  status: RecordStatus;
  /** Non-null on lists shared *with* the caller. Null for lists the
   *  caller owns. */
  shared_by: SharedByView | null;
  /** Effective access the caller has on this list. */
  my_access: EffectiveAccess;
}

export interface TaskListListResponse {
  lists: TaskListResponse[];
}

export interface TaskListSectionResponse {
  id: string;
  name: string;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface TaskListSectionListResponse {
  sections: TaskListSectionResponse[];
}

export interface UpdateListPayload {
  /** Tri-state: omit = leave; `null` clears (only for color today). */
  name?: string;
  color?: TaskListColor | null;
  description?: string;
  default_tag_names?: string[];
}

export interface UpdateListPrefPayload {
  /** `null` clears the section assignment. */
  section_id?: string | null;
  pinned?: boolean;
  position?: number;
}

export interface TaskResponse {
  id: string;
  list_id: string;
  assignee_id: string | null;
  title: string;
  description: string;
  priority: TaskPriority;
  /** Enriched tag objects — read shape only. */
  tags: TagResponse[];
  start_date: string | null;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  color: string;
  status: RecordStatus;
}

export interface TaskListItemsResponse {
  tasks: TaskResponse[];
}

export interface CreateTaskPayload {
  title: string;
  description?: string;
  priority?: TaskPriority;
  tags?: string[];
  assignee_id?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  color?: string;
}

/**
 * Tri-state PATCH semantics:
 *
 * - field omitted → leave existing
 * - field present and `null` → clear (only valid for nullable fields)
 * - concrete value → set
 *
 * The client only sends fields that are present in the payload object.
 */
export interface UpdateTaskPayload {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  tags?: string[];
  assignee_id?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  color?: string;
}

export interface ListTasksFilters {
  /** Comma-joined on the wire as `?priority=urgent,high`. */
  priority?: readonly TaskPriority[];
  /**
   * Sent as repeated query params: `?tag=foo&tag=bar`.
   *
   * NOTE: ApiClient.buildUrl uses URLSearchParams.set (not .append), so it
   * cannot emit repeated keys. Tags are therefore appended manually to the
   * path string here, keeping ApiClient unchanged.
   */
  tag?: readonly string[];
}

export const tasksApi = (client: ApiClient) => ({
  listLists(
    status?: RecordStatus,
    scope?: TasksScope,
  ): Promise<TaskListListResponse> {
    const params = new URLSearchParams();
    if (status !== undefined) params.set("status", status);
    if (scope !== undefined) params.set("scope", scope);
    const qs = params.toString();
    const path = qs ? `/task-lists?${qs}` : "/task-lists";
    return client.request<TaskListListResponse>(path, { method: "GET" });
  },
  archiveList(listId: string): Promise<TaskListResponse> {
    return client.request<TaskListResponse>(`/task-lists/${listId}/archive`, {
      method: "POST",
    });
  },
  restoreList(listId: string): Promise<TaskListResponse> {
    return client.request<TaskListResponse>(`/task-lists/${listId}/restore`, {
      method: "POST",
    });
  },
  createList(name: string): Promise<TaskListResponse> {
    return client.request<TaskListResponse>("/task-lists", {
      method: "POST",
      json: { name },
    });
  },
  renameList(listId: string, name: string): Promise<TaskListResponse> {
    return client.request<TaskListResponse>(`/task-lists/${listId}`, {
      method: "PATCH",
      json: { name },
    });
  },
  updateList(
    listId: string,
    patch: UpdateListPayload,
  ): Promise<TaskListResponse> {
    return client.request<TaskListResponse>(`/task-lists/${listId}`, {
      method: "PATCH",
      json: patch,
    });
  },
  updateListPref(
    listId: string,
    patch: UpdateListPrefPayload,
  ): Promise<TaskListResponse> {
    return client.request<TaskListResponse>(`/task-lists/${listId}/pref`, {
      method: "PATCH",
      json: patch,
    });
  },
  deleteList(listId: string): Promise<void> {
    return client.request<void>(`/task-lists/${listId}`, { method: "DELETE" });
  },

  listSections(): Promise<TaskListSectionListResponse> {
    return client.request<TaskListSectionListResponse>("/task-list-sections", {
      method: "GET",
    });
  },
  createSection(name: string): Promise<TaskListSectionResponse> {
    return client.request<TaskListSectionResponse>("/task-list-sections", {
      method: "POST",
      json: { name },
    });
  },
  renameSection(
    sectionId: string,
    name: string,
  ): Promise<TaskListSectionResponse> {
    return client.request<TaskListSectionResponse>(
      `/task-list-sections/${sectionId}`,
      { method: "PATCH", json: { name } },
    );
  },
  reorderSections(
    sectionIds: readonly string[],
  ): Promise<TaskListSectionListResponse> {
    return client.request<TaskListSectionListResponse>(
      "/task-list-sections/reorder",
      { method: "PATCH", json: { section_ids: [...sectionIds] } },
    );
  },
  deleteSection(sectionId: string): Promise<void> {
    return client.request<void>(`/task-list-sections/${sectionId}`, {
      method: "DELETE",
    });
  },
  listTasks(
    listId: string,
    filters?: ListTasksFilters,
  ): Promise<TaskListItemsResponse> {
    // Build query params manually so that `tag` can be repeated
    // (?tag=foo&tag=bar) — ApiClient only supports scalar query values.
    const params = new URLSearchParams();
    if (filters?.priority && filters.priority.length > 0) {
      params.set("priority", filters.priority.join(","));
    }
    if (filters?.tag) {
      for (const t of filters.tag) {
        params.append("tag", t);
      }
    }
    const qs = params.toString();
    const path = qs
      ? `/task-lists/${listId}/tasks?${qs}`
      : `/task-lists/${listId}/tasks`;
    return client.request<TaskListItemsResponse>(path, { method: "GET" });
  },
  createTask(
    listId: string,
    payload: CreateTaskPayload,
  ): Promise<TaskResponse> {
    return client.request<TaskResponse>(`/task-lists/${listId}/tasks`, {
      method: "POST",
      json: payload,
    });
  },
  update(
    listId: string,
    taskId: string,
    patch: UpdateTaskPayload,
  ): Promise<TaskResponse> {
    return client.request<TaskResponse>(
      `/task-lists/${listId}/tasks/${taskId}`,
      { method: "PATCH", json: patch },
    );
  },
  completeTask(listId: string, taskId: string): Promise<TaskResponse> {
    return client.request<TaskResponse>(
      `/task-lists/${listId}/tasks/${taskId}/complete`,
      { method: "POST" },
    );
  },
  uncompleteTask(listId: string, taskId: string): Promise<TaskResponse> {
    return client.request<TaskResponse>(
      `/task-lists/${listId}/tasks/${taskId}/uncomplete`,
      { method: "POST" },
    );
  },
  deleteTask(listId: string, taskId: string): Promise<void> {
    return client.request<void>(`/task-lists/${listId}/tasks/${taskId}`, {
      method: "DELETE",
    });
  },

  /** Flat search across all lists the user can access. */
  search(filters: {
    q?: string;
    list_id?: string;
    completed?: boolean;
    limit?: number;
  } = {}): Promise<TaskListItemsResponse> {
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.list_id) params.set("list_id", filters.list_id);
    if (filters.completed !== undefined) params.set("completed", String(filters.completed));
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    const qs = params.toString();
    const path = qs ? `/tasks?${qs}` : "/tasks";
    return client.request<TaskListItemsResponse>(path, { method: "GET" });
  },

  /** Flat single-task lookup (no list_id needed). */
  getFlat(taskId: string): Promise<TaskResponse> {
    return client.request<TaskResponse>(`/tasks/${taskId}`, { method: "GET" });
  },
});
